import Vault from '../../lib/Vault'
import axios from 'axios'
import { AUTHENTICATION_SERVICE, SITE } from '../../config/constants/env'
import { get } from 'lodash'
import { BehaviorSubject, Observable } from 'rxjs'

import FlakeId from 'flake-idgen'
import intformat from 'biguint-format'

export interface IAuthenticationState {
  status: 'AUTHENTICATED' | 'NOT_AUTHENTICATED'
}

export class AuthenticationControllerService {
  observable: BehaviorSubject<IAuthenticationState>

  constructor() {
    this.observable = new BehaviorSubject<IAuthenticationState>({
      status: 'NOT_AUTHENTICATED'
    })
  }

  subscribe(handler: (state: IAuthenticationState) => void) {
    this.observable.subscribe(handler)
  }

  state(): Observable<IAuthenticationState> {
    return this.observable.asObservable()
  }

  currentState(): IAuthenticationState {
    return this.observable.getValue()
  }

  storePkceToken(): string {
    const flakeIdGen = new FlakeId()

    const pkce = intformat(flakeIdGen.next(), 'dec')

    Vault.savePKCEToken(pkce)

    return pkce
  }

  getPKCEToken(): string {
    return Vault.getPKCEToken()
  }

  saveAccessToken(accessToken: string) {
    Vault.setAccessToken(accessToken)
  }

  fetchAuthorizationToken(redirectTo?: string) {
    const challenge = this.storePkceToken()

    window.location.href = `${AUTHENTICATION_SERVICE.API_URL}/authorize?client_id=TalentApp&redirect_url=${
      SITE.WEBAPP_URL
    }/callback${redirectTo ? redirectTo : ''}&response_type=authorization&code_challenge=${challenge}`
  }

  goToSignUpEndpoint() {
    const challenge = this.storePkceToken()

    window.location.href = `${AUTHENTICATION_SERVICE.WEB_URL}/sign-up?client_id=TalentApp&redirect_url=${SITE.WEBAPP_URL}/callback&response_type=authorization&code_challenge${challenge}`
  }

  async fetchAccessToken(authorizationToken: string): Promise<[number, string | null]> {
    const pkce = this.getPKCEToken()
    if (!pkce) {
      return [204, 'No Content']
    }
    try {
      const response = await axios({
        baseURL: `${AUTHENTICATION_SERVICE.API_URL}/api`,
        url: `/token?client_id=TalentApp&code=${authorizationToken}&code_challenge=${pkce}`,
        method: 'POST',
        withCredentials: true
      })

      const { access_token } = response.data

      Vault.clearPKCEToken()
      this.saveAccessToken(access_token)
      return [200, null]
    } catch (error) {
      if (error.response) {
        const errorMessage = [404, 500].includes(error.response.status)
          ? error.message
          : get(error, 'response.data') || error.message
        return [error.response.status, errorMessage]
      }
      return [500, error.message]
    }
  }

  userSignOut() {
    window.location.href = `${AUTHENTICATION_SERVICE.API_URL}/session/sign-out`
  }

  signOut() {
    Vault.clearAccessToken()
    this.fetchAuthorizationToken()
  }

  release() {}
}

let instance: AuthenticationControllerService | null = null

export default (() => {
  if (!instance) {
    instance = new AuthenticationControllerService()

    instance.release = () => {
      instance = null
    }
  }
  return instance
})()
